import React from "react";
import { Box, Typography } from "@mui/material";
import logo from "../../Logo/Logo.png";
import { API_BASE_URL } from "../../utils/api"; // keep consistent with your project
import DOMPurify from 'dompurify';

const formatDate = (d) =>
  new Date(d || Date.now()).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const ArchitecturalLetterTemplate = ({
  templateName,
  member = {},
  requestDetails = {},
  date,
  content = "",
  formattedAddress,
  deliveryMethod,
  logoUrl,
}) => {
  const today = formatDate(date);

  // ✅ placeholders (same approach as violation)
  const tags = {
    date: today,
    name: `${member?.FirstName || ""} ${member?.LastName || ""}`.trim(),
    address:
      formattedAddress ||
      member?.FullAddressHtml ||
      member?.FullAddress ||
      "",
    email: member?.EmailAddress || "",
    property_id: requestDetails?.property_id || "",
    id: requestDetails?.id ? `REQ-${requestDetails.id}` : "",
    description: requestDetails?.description || "",
    status: requestDetails?.status || "",
    priority: requestDetails?.priority || "",
    requested_at: requestDetails?.requested_at
      ? formatDate(requestDetails.requested_at)
      : "",
    conditions: requestDetails?.conditions || "",
    completion_days:
      requestDetails?.completion_days_current?.days_to_complete ?? "",
  };

  const applyTags = (html) => {
    if (!html) return "";
    let output = html;

    Object.entries(tags).forEach(([key, value]) => {
      const safeValue =
        typeof value === "string" ? value.replace(/\n/g, "<br/>") : value;

      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      output = output.replace(regex, safeValue ?? "");
    });

    return output;
  };

  // ✅ Manual page breaks: <hr class="page-break" />
  const splitContent = (html) =>
    html.split(/<hr[^>]*class=["']?page-break["']?[^>]*>/gi);

  const parts = splitContent(applyTags(content));

  // ✅ Documentation images pages (same pattern as violation)
  // Adjust these keys to whatever your API returns (file_url/file_name/file_type)
  const files = requestDetails?.files || requestDetails?.request_files || [];
  const imageFiles = (files || []).filter((f) =>
    String(f?.file_type || "").startsWith("image/")
  );

  const chunkImages = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  const imagePages = chunkImages(imageFiles, 2);

  const title = templateName || "Architectural Review Letter";
  const subtitle = deliveryMethod || "Delivered via US Mail and Email"; // or `Issued ${today}` if you prefer

  // ✅ Server image URL builder (match your backend route)
  // Update folder/route name to your real one.
  const buildImageUrl = (file) => {
    if (file?.file_url?.startsWith("http")) return file.file_url;

    // Common patterns:
    // - if you store file_url already like "/Uploads/ArchitecturalRequests/xxx.jpg" use API_BASE_URL + file_url
    if (file?.file_url?.startsWith("/")) return `${API_BASE_URL}${file.file_url}`;

    // - otherwise fallback to a route you serve
    return `${API_BASE_URL}/architecturalimages/${requestDetails.id}/${encodeURIComponent(
      file.file_name || ""
    )}`;
  };

  return (
    <Box
      id="styled-letter"
      sx={{
        backgroundColor: "#fff",
        color: "#222",
        maxWidth: 760,
        mx: "auto",
        fontFamily: `"Nunito Sans", "Helvetica Neue", Arial, sans-serif`,
        fontSize: "11pt",
        lineHeight: 1.6,
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <style>{`
        .page {
  break-after: page;
  page-break-after: always;
  padding: 32px 48px;
}
.page:last-child {
  break-after: auto;
  page-break-after: auto;
}
.pdf-header{
  width: 100%;
  text-align: center;
  margin: 0 0 6px 0;       /* keep it tight */
  padding: 0 0 6px 0;      /* small space before the line */
  border-bottom: 1px solid #ccc;
}

.pdf-header .title,
.pdf-header .subtitle{
  display: block;
  width: 100%;
  text-align: center !important;
  margin: 0 !important;
}

.pdf-header .title{ line-height: 1.1; }
.pdf-header .subtitle{ line-height: 1.1; }

.pdf-header img {
  height: 80px;
  margin: 0 auto;             /* center image */
  display: block;
}

.pdf-header .title {
  font-weight: bold;
  font-size: 14pt;
  margin: 0 !important;       /* kill Typography default spacing */
  padding: 0 !important;
  line-height: 1 !important;  /* tight */
}

.pdf-header .subtitle {
  font-size: 9.5pt;
  color: #555;
  margin: 0 !important;       /* kill Typography default spacing */
  padding: 0 !important;
  line-height: 1 !important;  /* tight */
}

/* if you still see a tiny gap between title/subtitle, force them to be blocks */
.pdf-header .title,
.pdf-header .subtitle {
  display: block;
}

#styled-letter p {
  margin: 10px 0;
  font-size: 11pt;
  text-align: justify;
  text-justify: inter-word;
}

#styled-letter ul {
  margin: 4px 0 10px 20px;
  padding: 0;
}
#styled-letter li {
  margin: 2px 0;
  line-height: 1.3;
}

/* ✅ Tight spacing just for the name/address header block */
#styled-letter .letter-header-block p {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.15 !important;
  text-align: left !important;
}
#styled-letter .letter-header-block {
  margin-bottom: 10px;
}

/* optional: keep closing/signature left aligned too */
#styled-letter .signature p {
  text-align: left !important;
}



      `}</style>

      {/* MAIN LETTER PAGES */}
      {parts.map((section, i) => (
        <React.Fragment key={i}>
          <div className="page">
            <Box className="pdf-header">
              <img src={logoUrl || logo} alt="HOA Logo" />
              <Typography className="title">{title}</Typography>
              <Typography className="subtitle">{subtitle}</Typography>
            </Box>

            <div
              style={{ fontSize: "inherit", lineHeight: "inherit" }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section || '') }}
            />
          </div>

          {/* DOCUMENTATION PAGES (after last text section) */}
          {i === parts.length - 1 && imageFiles.length > 0 && (
            <>
              {imagePages.map((chunk, pageIndex) => (
                <div className="page" key={`img-page-${pageIndex}`}>
                  <Box className="pdf-header">
                    <img src={logoUrl || logo} alt="HOA Logo" />
                    <Typography className="title">{title}</Typography>
                    <Typography className="subtitle">{subtitle}</Typography>
                  </Box>

                  {pageIndex === 0 && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        fontSize: "12pt",
                        borderBottom: "2px solid #ddd",
                        pb: 0.5,
                      }}
                    >
                      Documentation:
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {chunk.map((file) => {
                      const fileUrl = buildImageUrl(file);

                      return (
                        <Box
                          key={file.id || file.file_name}
                          sx={{
                            p: 1.5,
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                            backgroundColor: "#fafafa",
                            width: "45%",
                            textAlign: "center",
                          }}
                        >
                          <img
                            src={fileUrl}
                            alt={file.file_name || "Architectural image"}
                            crossOrigin="anonymous"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "250px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </div>
              ))}
            </>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default ArchitecturalLetterTemplate;
